import { ApiError, type components, unwrap } from '@shared/api-client/index';
import { clearPersistedQueryCache } from '@shared/ui/query/query-provider.component';
import { toastApiError } from '@shared/ui/toast/toast-store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { billingStatusKey, invoicesKey } from '../billing/billing.hooks';
import { invitationsKey } from '../members/invitations.hooks';
import { membersKey } from '../members/members.hooks';
import { api, uploadFile } from './api';
import { QUERY_CACHE_KEY } from './cache';

export type Me = components['schemas']['MeResponse'];

/** One account this browser is signed into (for the switcher). */
export type Account = components['schemas']['AccountResponse'];

export const meKey = ['me'] as const;
const accountsKey = ['accounts'] as const;

/**
 * The signed-in principal, or null when logged out — a 401 is the logged-out
 * state, not an error. Client-only: the session cookie lives in the browser, so
 * revalidate on mount (in dev the api cookie isn't on the app origin).
 */
export function useMe() {
	return useQuery({
		queryKey: meKey,
		queryFn: async () => {
			try {
				return unwrap(await api.GET('/v1/me'));
			} catch (error) {
				if (error instanceof ApiError && error.status === 401) return null;
				throw error;
			}
		},
		staleTime: 0,
	});
}

/** The accounts this browser is signed into, active first — drives the switcher. */
export function useAccounts() {
	return useQuery({
		queryKey: accountsKey,
		queryFn: async () => unwrap(await api.GET('/v1/auth/accounts')),
		staleTime: 0,
	});
}

/**
 * Switch the active account. The server just swaps the session cookie, so drop every
 * cached query (this account's data is stale under the new cookie) and hard-navigate to
 * the root, landing signed in as the switched-to account.
 */
export function useSwitchAccount() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (userId: string) =>
			unwrap(await api.POST('/v1/auth/switch-account', { body: { userId } })),
		onError: toastApiError,
		onSuccess: () => {
			queryClient.clear();
			clearPersistedQueryCache(QUERY_CACHE_KEY);
			window.location.assign('/');
		},
	});
}

/**
 * Sign out the active account, then hard-navigate to the root. Clears every cached query
 * (in memory + the persisted copy) so the next visitor on a shared browser can't restore
 * ours, then reloads `/`. Whatever the server left signed in — the next account it promoted
 * to active, or nobody — `/` re-runs every query under the new cookie and renders the right
 * thing: the promoted account's hub, or the signed-out front door once `/me` now reads 401.
 * Reloading (rather than a setQueryData(meKey, null)) also sidesteps a stale useMe observer
 * that queryClient.clear() leaves behind: after clear() the mounted observer doesn't pick up
 * a later setQueryData, so a sole-account sign-out would otherwise stick on the signed-in hub
 * until a manual reload. The switch-account path reloads for the same reason.
 */
export function useSignOut() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async () => {
			unwrap(await api.POST('/v1/auth/logout'));
		},
		onSuccess: () => {
			queryClient.clear();
			clearPersistedQueryCache(QUERY_CACHE_KEY);
			window.location.assign('/');
		},
	});
}

export function useSwitchWorkspace() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (workspaceId: string) =>
			unwrap(await api.POST('/v1/auth/switch-workspace', { body: { workspaceId } })),
		onSuccess: () => {
			// The active workspace just changed, so every workspace-scoped cache still
			// holds the OLD workspace's rows. Left in place they would render stale and,
			// worse, let a mutation act on a stale userId against the NEW session. Drop
			// them outright (removeQueries, not invalidate) so nothing stale can render
			// or be acted on before the fresh fetch lands. membersKey is the prefix of
			// the seat roster too, so this clears both. Then refetch /me.
			queryClient.removeQueries({ queryKey: membersKey });
			queryClient.removeQueries({ queryKey: invitationsKey });
			queryClient.removeQueries({ queryKey: billingStatusKey });
			queryClient.removeQueries({ queryKey: invoicesKey });
			// removeQueries only touches memory; drop the persisted copy too so a reload
			// inside the persister's throttle window can't restore the old workspace's
			// roster/billing. The provider re-persists the fresh cache once it lands.
			clearPersistedQueryCache(QUERY_CACHE_KEY);
			return queryClient.invalidateQueries({ queryKey: meKey });
		},
	});
}

/** Progressive profiling: set a display name after signup (self-only PATCH /me). */
export function useUpdateName() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (name: string) => unwrap(await api.PATCH('/v1/me', { body: { name } })),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: meKey }),
	});
}

/**
 * Upload a new avatar (self-only POST /me/avatar). Routes through the typed client
 * via `uploadFile` so the CSRF header + session cookie ride along (a hand-rolled
 * fetch omits the CSRF header and the api 403s it); the api validates + resizes
 * and returns the updated user. Refetch /me on success so the new avatar shows
 * everywhere it's rendered.
 */
export function useUploadAvatar() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (file: File) => unwrap(await uploadFile('/v1/me/avatar', {}, file)),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: meKey }),
	});
}

/** Remove the current avatar (self-only DELETE /me/avatar). */
export function useRemoveAvatar() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async () => unwrap(await api.DELETE('/v1/me/avatar')),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: meKey }),
	});
}
